import kurentoUtils from 'kurento-utils';
import {find, forEach, without} from 'lodash';

import {GroupRoomManager, WebRTCPeerOptions} from './ConferenceRoom';
import {PeerConnectionId} from './constants';

export class ParticipantPeer {
    rtcPeer: kurentoUtils.WebRtcPeer;
    started: boolean;
    private mediaElement: HTMLAudioElement | HTMLVideoElement;
    private element: HTMLElement;

    get constraints() {
        return this.options.constraints || {audio: true, video: true};
    }

    get hasAudioConstraints() {
        return !!this.constraints.audio;
    }

    get hasVideoConstraints() {
        return !!this.constraints.video;
    }

    private get mediaElementName() {
        return this.hasVideoConstraints ? 'video' : this.hasAudioConstraints ? 'audio' : 'video';
    }

    constructor(public id: PeerConnectionId, public options: WebRTCPeerOptions) {
    }

    getMediaElement = () => this.mediaElement;

    setMainRemoteParticipantAudioOutput = (deviceId: string) => {
        const mediaElements = this.options.mainRemoteParticipantElement?.getElementsByTagName(
            this.mediaElementName
        );
        Array.from(mediaElements).forEach((element: any) => {
            if ('sinkId' in element) {
                element?.setSinkId?.(deviceId);
            }
        });
    };

    attach(element: HTMLElement) {
        if (element) {
            const mediaElement = document.createElement(this.mediaElementName);
            this.mediaElement = mediaElement;

            this.element = element;
            this.element.appendChild(mediaElement);

            mediaElement.autoplay = true;
            mediaElement.controls = false;
            mediaElement.setAttribute('playsinline', '');
            forEach(this.options?.mediaElementProperties, (attrValue, attrName) => {
                mediaElement[attrName] = attrValue;
            });

            if (this.options?.audiooutput) {
                this.setMainRemoteParticipantAudioOutput(this.options?.audiooutput);
            }
        }
    }

    attachStream = (stream: MediaStream) => {
        this.getMediaElement().srcObject = stream;
    };

    dispose = () => {
        this.rtcPeer?.dispose();

        if (this.mediaElement) {
            this.mediaElement.srcObject = null;
            this.mediaElement.remove();
            this.mediaElement = null;
        }
    };

    stopLocalTracks = () => {
        this.rtcPeer?.getLocalStream()?.getTracks().forEach((track) => track.stop());
    };
}

export class Participant {
    peers: ParticipantPeer[] = [];

    constructor(protected room: GroupRoomManager, public name: string) {
    }

    getPeerConnection = (id: PeerConnectionId) => find(this.peers, {id});

    addPeerConnection = (id: PeerConnectionId, options: WebRTCPeerOptions) => {
        const peer = new ParticipantPeer(id, options);
        this.peers = [...this.peers, peer];
        return peer;
    };

    removePeerConnection = (id: PeerConnectionId) => {
        const peer = find(this.peers, {id});
        peer?.dispose();
        this.peers = without(this.peers, peer);
        return peer;
    };

    dispose = () => {
        this.peers.forEach((peer) => {
            peer.dispose();
        });
        this.peers = [];
    };
}
